import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarClock, RefreshCw, Trash2 } from "lucide-react";
import axios, { API, fmt } from "./commoditiesLib";

// 2026-11-02 — chat: "Can we use News Monitor? especially commodities are mostly news driven"
// -> event calendar. LOG ONLY: scheduled releases that move MCX (EIA crude inventories, US jobs,
// CPI, Fed, OPEC+) from event_calendar.py. Shows what is coming, and how commodity paper
// trades held over an event did vs the rest. Gates nothing.

const FAM_LABEL = { CRUDEOIL: "Crude", GOLD: "Gold", SILVER: "Silver", NATURALGAS: "Nat gas", "*": "Other" };
const IMP_COLOR = { high: "var(--bear)", medium: "#FFB66E" };

const away = (iso, now) => {
  const m = Math.round((new Date(iso).getTime() - now) / 60000);
  if (m < -1) return `${Math.abs(m) < 60 ? `${Math.abs(m)}m` : `${Math.round(Math.abs(m) / 60)}h`} ago`;
  if (m <= 1) return "now";
  if (m < 60) return `in ${m}m`;
  const h = Math.floor(m / 60);
  if (h < 48) return `in ${h}h ${m % 60}m`;
  return `in ${Math.round(h / 24)}d`;
};

const Stat = ({ s }) => (!s || !s.trades ? <span className="text-muted-3">—</span> : (
  <span>
    {s.trades}t · {fmt(s.win_rate_pct, 0)}% win · avg {fmt(s.avg_pnl_pct, 1)}% · PF {s.profit_factor ?? "—"}
    {s.stop_pct != null ? ` · stops ${fmt(s.stop_pct, 0)}%` : ""}
  </span>
));

const PF = ({ s }) => (!s || !s.trades ? <span className="text-muted-3">—</span> : (
  <span>{s.trades}t · PF {s.profit_factor ?? "—"} · avg {fmt(s.avg_pnl_pct, 1)}%</span>
));

const LAB_ROWS = [
  ["all", "All trades"],
  ["held_over_event", "Held over a high event"],
  ["no_event", "No event in the hold"],
  ["entered_60m_before", "Entered ≤60 min before one"],
  ["rule_no_entry_30m_before", "Rule: no entry 30 min before"],
  ["rule_no_entry_60m_before", "Rule: no entry 60 min before"],
  ["rule_no_entry_120m_before", "Rule: no entry 120 min before"],
  ["rule_exit_before_event", "Rule: exit before the event"],
];

// commodity_event_lab.py — 1-year research run (Jobs page → Commodity event lab → Run now)
const LabResults = ({ lab }) => {
  if (!lab) {
    return <div className="text-muted-3 pt-2 border-t border-grid/60">1-year lab: not run yet (Jobs → “Commodity event lab” → Run now).</div>;
  }
  return (
    <div className="pt-2 border-t border-grid/60 space-y-3">
      <div className="text-muted-2 font-semibold">
        1-year lab · run {new Date(lab.created_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}
        {(lab.errors || []).length ? <span style={{ color: "var(--bear)" }}> · {lab.errors.join("; ")}</span> : null}
      </div>
      {Object.entries(lab.results || {}).map(([und, r]) => (
        <div key={und} className="space-y-1">
          <div className="text-muted-2">{und} · {r.from} → {r.to} · {r.source} · exits −{r.exits?.sl_pct}% / +{r.exits?.tp_pct}% of premium</div>
          <table className="w-full">
            <thead className="text-muted-3 text-[9px] uppercase tracking-[0.15em]">
              <tr><th className="text-left">Event</th><th className="text-right">n</th><th className="text-right">IST</th>
                <th className="text-right">30m range vs normal</th><th className="text-right">60m</th><th className="text-right">60m before</th></tr>
            </thead>
            <tbody>
              {Object.entries(r.event_study || {}).map(([k, s]) => (
                <tr key={k} className="border-b border-grid/30">
                  <td>{k}</td><td className="text-right">{s.events}</td>
                  <td className="text-right text-muted-3">{(s.clock_ist || []).join(" ")}</td>
                  <td className="text-right">{s.after_30m?.range_ratio != null ? `${s.after_30m.range_ratio}×` : "—"}
                    <span className="text-muted-3"> ({fmt(s.after_30m?.event_range_pct, 2)}% vs {fmt(s.after_30m?.normal_range_pct, 2)}%)</span></td>
                  <td className="text-right">{s.after_60m?.range_ratio != null ? `${s.after_60m.range_ratio}×` : "—"}</td>
                  <td className="text-right">{s.before_60m?.range_ratio != null ? `${s.before_60m.range_ratio}×` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <table className="w-full">
            <thead className="text-muted-3 text-[9px] uppercase tracking-[0.15em]">
              <tr><th className="text-left">Autopilot rules replayed (both strategies)</th><th className="text-right">Whole year</th>
                <th className="text-right">1st half</th><th className="text-right">2nd half</th></tr>
            </thead>
            <tbody>
              {LAB_ROWS.map(([k, label]) => (
                <tr key={k} className="border-b border-grid/30">
                  <td className="text-muted-3">{label}</td>
                  <td className="text-right"><PF s={r.combined?.all?.[k]} /></td>
                  <td className="text-right"><PF s={r.combined?.first_half?.[k]} /></td>
                  <td className="text-right"><PF s={r.combined?.second_half?.[k]} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
      <div className="text-muted-3">Option P&amp;L is modelled (premium from 20-day volatility, delta 0.5) — compare rows with each other, not with real P&amp;L.</div>
    </div>
  );
};

export const EventCalendarCard = ({ isAdmin = false, compact = false }) => {
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [lab, setLab] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [form, setForm] = useState({ title: "", date: "", time: "", families: ["CRUDEOIL"] });

  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/event-calendar/upcoming`, { params: { days: 7 } });
      setData(d);
    } catch {
      setData(null);
    }
  }, []);

  useEffect(() => {
    load();
    const t1 = setInterval(() => { if (!document.hidden) load(); }, 300000);
    const t2 = setInterval(() => setNow(Date.now()), 30000);
    return () => { clearInterval(t1); clearInterval(t2); };
  }, [load]);

  const loadSummary = async () => {
    setShowResults((v) => !v);
    if (summary) return;
    const [s, l] = await Promise.allSettled([
      axios.get(`${API}/event-calendar/summary`, { params: { days: 180 } }),
      axios.get(`${API}/event-calendar/lab`),
    ]);
    if (s.status === "fulfilled") setSummary(s.value.data);
    else toast.error(s.reason?.response?.data?.detail || "Couldn't load event results");
    if (l.status === "fulfilled") setLab(l.value.data?.run || null);
  };

  const refresh = async () => {
    setBusy(true);
    try {
      const { data: r } = await axios.post(`${API}/event-calendar/refresh`);
      toast.success(`Calendar refreshed · feed ${r.ff} · BLS ${r.bls}${r.errors?.length ? ` · ${r.errors.join("; ")}` : ""}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally {
      setBusy(false);
    }
  };

  const add = async () => {
    try {
      const { data: r } = await axios.post(`${API}/event-calendar/manual`, { ...form, importance: "high" });
      if (!r.ok) { toast.error(r.message || "Not added"); return; }
      toast.success("Event added");
      setForm({ title: "", date: "", time: "", families: ["CRUDEOIL"] });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await axios.delete(`${API}/event-calendar/manual/${encodeURIComponent(id)}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (!data) return null;
  const upcoming = (data.events || []).filter((e) => new Date(e.at_utc).getTime() >= now - 3 * 3600000);
  const nextHigh = Object.entries(data.next_high || {}).filter(([, e]) => e);

  const headline = (
    <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs font-mono-data">
      {nextHigh.map(([fam, e]) => {
        const mins = (new Date(e.at_utc).getTime() - now) / 60000;
        const soon = mins >= 0 && mins <= 60;
        return (
          <span key={fam} style={soon ? { color: "var(--bear)" } : undefined}>
            <span className="text-muted-3">{FAM_LABEL[fam] || fam}:</span> {e.title} · {e.at_ist} IST{" "}
            <span className={soon ? "font-semibold" : "text-muted-2"}>({away(e.at_utc, now)})</span>
          </span>
        );
      })}
    </div>
  );

  if (compact) {
    return (
      <div className="border border-grid px-4 py-2 space-y-1" data-testid="event-calendar-compact">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data flex items-center gap-2">
          <CalendarClock size={11} /> Next high-impact event
        </div>
        {headline}
      </div>
    );
  }

  let lastDay = null;
  return (
    <div className="border border-grid p-5 space-y-3" data-testid="event-calendar-card">
      <div className="flex items-center justify-between gap-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data flex items-center gap-2">
          <CalendarClock size={12} /> Scheduled events · next 7 days · IST · log only
          {isAdmin && (
            <button onClick={refresh} disabled={busy} title="Fetch the feeds now"
                    className="text-muted-3 hover:text-muted-1 disabled:opacity-50">
              <RefreshCw size={11} className={busy ? "animate-spin" : ""} />
            </button>
          )}
        </div>
        <button onClick={loadSummary}
                className="text-[10px] uppercase tracking-[0.15em] font-mono-data text-muted-3 underline">
          {showResults ? "Hide" : "Show"} trade results around events
        </button>
      </div>

      {headline}

      {showResults && (
        <div className="border border-grid/60 p-3 text-[11px] font-mono-data space-y-2" data-testid="event-calendar-results">
          {!summary ? <div className="text-muted-3">Loading…</div> : !summary.trades ? (
            <div className="text-muted-3">No closed commodity paper trades in the last {summary.days} days.</div>
          ) : Object.entries(summary.by_family || {}).map(([fam, g]) => (
            <div key={fam}>
              <div className="text-muted-2 font-semibold">{FAM_LABEL[fam] || fam} · last {summary.days} days</div>
              <div><span className="text-muted-3">Held over a high event: </span><Stat s={g.held_over_event} /></div>
              <div><span className="text-muted-3">No event in the hold: </span><Stat s={g.no_event} /></div>
              <div><span className="text-muted-3">Entered ≤60 min before one: </span><Stat s={g.entered_60m_before} /></div>
              {Object.entries(g.by_event || {}).map(([k, s]) => (
                <div key={k} className="pl-3 text-muted-3">{k}: <Stat s={s} /></div>
              ))}
            </div>
          ))}
          <div className="text-muted-3">Small samples — a pattern needs dozens of trades on each side before it becomes a rule.</div>
          <LabResults lab={lab} />
        </div>
      )}

      {upcoming.length === 0 ? (
        <div className="text-xs text-muted-2 font-mono-data">No events in the next 7 days.</div>
      ) : (
        <table className="w-full text-[11px] font-mono-data">
          <tbody>
            {upcoming.map((e, i) => {
              const day = e.at_ist.slice(0, 10);
              const showDay = day !== lastDay;
              lastDay = day;
              const past = new Date(e.at_utc).getTime() < now;
              return (
                <React.Fragment key={`${e.key}-${e.at_utc}-${i}`}>
                  {showDay && (
                    <tr><td colSpan={5} className="pt-2 text-[10px] uppercase tracking-[0.15em] text-muted-3">{day}</td></tr>
                  )}
                  <tr className={`border-b border-grid/40 ${past ? "opacity-50" : ""}`}>
                    <td className="py-1 w-14">{e.at_ist.slice(11)}{e.approx ? <span className="text-muted-3" title="Time from the usual schedule, not a published calendar">~</span> : null}</td>
                    <td>{e.title}</td>
                    <td className="text-muted-2">
                      {Object.entries(e.affects || {}).map(([fam, imp]) => (
                        <span key={fam} className="mr-2" style={{ color: IMP_COLOR[imp] }}>{FAM_LABEL[fam] || fam}</span>
                      ))}
                    </td>
                    <td className="text-muted-3">
                      {e.forecast ? `fcst ${e.forecast}` : ""}{e.previous ? ` · prev ${e.previous}` : ""}
                    </td>
                    <td className="text-right text-muted-3">
                      {away(e.at_utc, now)}
                      {isAdmin && e.source === "manual" && (
                        <button onClick={() => remove(e.id)} className="ml-2 text-muted-3 hover:text-muted-1" title="Delete">
                          <Trash2 size={10} />
                        </button>
                      )}
                    </td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}

      {isAdmin && (
        <div className="flex flex-wrap items-center gap-2 text-[11px] font-mono-data pt-1">
          <span className="text-muted-3">Add (e.g. OPEC+ meeting):</span>
          <input value={form.title} onChange={(ev) => setForm({ ...form, title: ev.target.value })}
                 placeholder="Title" className="bg-transparent border border-grid px-2 py-1 w-48" />
          <input type="date" value={form.date} onChange={(ev) => setForm({ ...form, date: ev.target.value })}
                 className="bg-transparent border border-grid px-2 py-1" />
          <input type="time" value={form.time} onChange={(ev) => setForm({ ...form, time: ev.target.value })}
                 className="bg-transparent border border-grid px-2 py-1" />
          {["CRUDEOIL", "GOLD", "SILVER", "NATURALGAS"].map((f) => (
            <label key={f} className="flex items-center gap-1 text-muted-2">
              <input type="checkbox" checked={form.families.includes(f)}
                     onChange={(ev) => setForm({ ...form, families: ev.target.checked
                       ? [...form.families, f] : form.families.filter((x) => x !== f) })} />
              {FAM_LABEL[f]}
            </label>
          ))}
          <button onClick={add} disabled={!form.title || !form.date || !form.time}
                  className="px-2 py-1 border border-grid uppercase tracking-[0.15em] disabled:opacity-50">Add</button>
        </div>
      )}

      <div className="text-[10px] text-muted-3 font-mono-data">
        Red = high importance for that commodity, amber = medium. ~ = usual-schedule time.
        {data.last_refresh?.at ? ` Feeds checked ${new Date(data.last_refresh.at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}.` : " Feeds not fetched yet — rules only."}
        {(data.last_refresh?.errors || []).length ? ` (${data.last_refresh.errors.join("; ")})` : ""}
      </div>
    </div>
  );
};

export default EventCalendarCard;
